import GridHeader from "@/components/GridHeader";
import SongGrid from "@/components/SongGrid";
import { SessionContext } from "@/context/SessionContext";
import {
	ActionIcon,
	Box,
	Button,
	Group, rem,
	Stack,
	Text,
	TextInput,
	useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowRight, IconSearch } from "@tabler/icons-react";
import { useContext, useMemo, useState } from "react";

const UserLibrary: React.FC = () => {
	const theme = useMantineTheme();
	const [libraryMode, setLibraryMode] = useState<string>("My Songs");
	const [search, setSearch] = useState<string>("");

	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("SongDetails must be a child of SessionContext.");
	}

	const filteredSongs = useMemo(() => {
		if (!session.userSongs) return [];
		if (!search) return session.userSongs;
		const query = search.toLowerCase();
		return session.userSongs.filter(
			(song) =>
				song.songName.toLowerCase().includes(query) ||
				(song.artists || []).some((artist) =>
					artist.artistName.toLowerCase().includes(query)
				)
		);
	}, [session.userSongs, search]);

	const activatedButton = {
		backgroundColor: theme.colors.blue[7],
		color: theme.colors.blue[0],
	};

	const nonActivatedButton = {
		backgroundColor: theme.colors.blue[0],
		color: theme.colors.blue[7],
	};

	return (
		<Stack align={"center"} justify={"center"}>
			<GridHeader>
				<Text
					p={20}
					ml={isMobile ? 10 : 20}
					fz={isMobile ? 15 : 45}
					c={theme.colors.blue[0]}
					ta="center"
					style={{
						minWidth: 0,
					}}>
					{libraryMode}
				</Text>
				<Box style={{ flex: 1, display: "flex", justifyContent: "center" }}>
					<TextInput
						radius="xl"
						size={isMobile ? "xs" : "md"}
						placeholder="Search library..."
						onChange={(e) => setSearch(e.currentTarget.value)}
						rightSectionWidth={42}
						leftSection={<IconSearch size={18} stroke={1.5} color={"#5A80A0"} />}
						rightSection={
							<ActionIcon size={isMobile ? 20 : 32} radius="xl" color={"#5A80A0"} variant="filled">
								<IconArrowRight size={isMobile ? 10 : 18} stroke={1.5} color={"#E8F1F9"} />
							</ActionIcon>
						}
						style={{ maxWidth: rem(260), width: "100%" }}
					/>
				</Box>
				<Group wrap="nowrap" gap="xs" mr={15}>
					<Button
						onClick={() => setLibraryMode("My Songs")}
						style={libraryMode === "My Songs" ? activatedButton : nonActivatedButton}
						p={0}
						w={isMobile ? "55" : "125"}
						fz={isMobile ? "5" : "15"}
					>
						My Songs
					</Button>
					<Button
						onClick={() => setLibraryMode("My Playlists")}
						style={libraryMode === "My Playlists" ? activatedButton : nonActivatedButton}
						p={0}
						w={isMobile ? "55" : "125"}
						fz={isMobile ? "5" : "15"}
					>
						My Playlists
					</Button>
				</Group>
			</GridHeader>

			<Box w={"100%"} mt={200}>
				{libraryMode === "My Songs" ? (
					<SongGrid songs={filteredSongs} type="library" skeletonSize={16} />
				) : (
					<Text>No Playlists</Text>
				)}
			</Box>
		</Stack>
	);
};

export default UserLibrary;